import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  // Main nav links - simplified
  const mainLinks = [
    { name: "Home", path: "/" },
    { name: "For Buyers", path: "/inspections-for-buyers" },
    { name: "For Sellers", path: "/inspections-for-sellers" },
    { name: "Contact", path: "/contact" },
  ];

  // Everything else in "More" dropdown
  const moreLinks = [
    { name: "About Us", path: "/about" },
    { name: "Our Team", path: "/team" },
    { name: "Partners", path: "/partners" },
    { name: "Pricing", path: "/pricing" },
    { name: "Blog", path: "/blog" },
    { name: "FAQ", path: "/faq" },
    { name: "Sample Reports", path: "/sample-reports" },
    { type: "divider", label: "Services" },
    { name: "Building Status Reports", path: "/services/building-status" },
    { name: "Building Inspection Reports", path: "/services/building-condition" },
    { name: "Pest Reports", path: "/services/pest" },
    { name: "Pool Remediation Reports", path: "/services/pool" },
    { name: "Plumbing Reports", path: "/services/plumbing" },
    { name: "Electrical Reports", path: "/services/electrical" },
    { type: "divider", label: "Service Areas" },
    { name: "Darwin", path: "/service-areas/darwin" },
    { name: "Palmerston", path: "/service-areas/palmerston" },
    { name: "Litchfield", path: "/service-areas/litchfield" },
    { name: "Wagait Beach", path: "/service-areas/wagait" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const isMoreActive = () => {
    return moreLinks.some(link => link.path && isActive(link.path));
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm" role="navigation" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link" aria-label="Aussie Inspections - Go to homepage">
            <img 
              src="/logo.png" 
              alt="Aussie Inspections" 
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1" role="menubar">
            {mainLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  isActive(link.path)
                    ? "text-primary bg-primary/5"
                    : "text-slate-600 hover:text-primary hover:bg-slate-100"
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* More Dropdown */}
            <div className="relative">
              <button
                onClick={() => setMoreOpen(!moreOpen)}
                onMouseEnter={() => setMoreOpen(true)}
                data-testid="more-dropdown-button"
                className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                  isMoreActive()
                    ? "text-primary bg-primary/5"
                    : "text-slate-600 hover:text-primary hover:bg-slate-100"
                }`}
              >
                More
                <ChevronDown className={`ml-1 w-4 h-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {moreOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onMouseLeave={() => setMoreOpen(false)}
                    className="absolute top-full right-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 max-h-[70vh] overflow-y-auto"
                    data-testid="more-dropdown-menu"
                  >
                    {moreLinks.map((item, index) => (
                      item.type === "divider" ? (
                        <div key={index} className="px-4 py-2 border-t border-slate-100 mt-1">
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            {item.label}
                          </span>
                        </div>
                      ) : (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMoreOpen(false)}
                          data-testid={`more-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                            isActive(item.path)
                              ? "text-primary bg-primary/5"
                              : "text-slate-600 hover:text-primary hover:bg-slate-50"
                          }`}
                        >
                          {item.name}
                        </Link>
                      )
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:+61413245519"
              className="flex items-center text-slate-600 hover:text-primary transition-colors"
              aria-label="Call us at 0413 24 55 19"
            >
              <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
              <span className="font-medium">0413 24 55 19</span>
            </a>
            <Button
              asChild
              className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase"
              data-testid="book-now-button"
            >
              <Link to="/contact">Book Now</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-slate-200 bg-white"
            role="menu"
          >
            <div className="px-4 py-4 space-y-2">
              {/* Main Links */}
              {mainLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`block px-4 py-3 rounded-md font-medium transition-colors ${
                    isActive(link.path)
                      ? "text-primary bg-primary/5"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* More Section */}
              <div>
                <button
                  onClick={() => setMoreOpen(!moreOpen)}
                  data-testid="mobile-more-button"
                  className={`flex items-center justify-between w-full px-4 py-3 rounded-md font-medium transition-colors ${
                    isMoreActive()
                      ? "text-primary bg-primary/5"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  More
                  <ChevronDown className={`w-4 h-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {moreOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-2 ml-4 space-y-1 max-h-64 overflow-y-auto"
                      data-testid="mobile-more-menu"
                    >
                      {moreLinks.map((item, index) => (
                        item.type === "divider" ? (
                          <div key={index} className="px-4 py-2 border-t border-slate-100 mt-2">
                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                              {item.label}
                            </span>
                          </div>
                        ) : (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => {
                              setIsOpen(false);
                              setMoreOpen(false);
                            }}
                            data-testid={`mobile-more-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`block px-4 py-2 text-sm rounded-md font-medium transition-colors ${
                              isActive(item.path)
                                ? "text-primary bg-primary/5"
                                : "text-slate-600 hover:bg-slate-100"
                            }`}
                          >
                            {item.name}
                          </Link>
                        )
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Phone & CTA */}
              <a
                href="tel:+61413245519"
                className="flex items-center px-4 py-3 text-slate-600 hover:text-primary rounded-md hover:bg-slate-100"
              >
                <Phone className="w-5 h-5 mr-3" />
                <span className="font-medium">0413 24 55 19</span>
              </a>
              
              <Button
                asChild
                className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold"
                data-testid="mobile-book-now-button"
              >
                <Link to="/contact" onClick={() => setIsOpen(false)}>Book Now</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
